import { useState } from "react";
import { motion } from "framer-motion";
import { Play, AudioLines, Camera, Mail } from "lucide-react";
import Reveal from "./Reveal";
import SectionHeading from "./SectionHeading";
import AudioPlayer from "./AudioPlayer";
import { EMBED_URL, THUMB_URL, THUMB_FALLBACK, IMAGES, EMAIL } from "../constants";

function VideoBlock() {
  const [playing, setPlaying] = useState(false);
  const [thumb, setThumb] = useState(THUMB_URL);

  return (
    <div
      className="relative rounded-3xl overflow-hidden border border-white/10 aspect-video group cursor-pointer"
      onClick={() => !playing && setPlaying(true)}
      data-testid="media-video-block"
    >
      {playing ? (
        <iframe
          src={EMBED_URL}
          title="Hulda Perez — Video Reel"
          className="absolute inset-0 w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          data-testid="media-reel-iframe"
        />
      ) : (
        <>
          <img
            src={thumb}
            onError={() => setThumb(THUMB_FALLBACK)}
            alt="Hulda Perez video reel"
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            data-testid="media-reel-thumbnail"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-transparent to-ink/30" />
          <div className="absolute inset-0 flex items-center justify-center">
            <motion.div
              whileHover={{ scale: 1.12 }}
              whileTap={{ scale: 0.94 }}
              className="w-20 h-20 rounded-full bg-hpink text-white flex items-center justify-center glow-pink"
              data-testid="media-play-reel"
            >
              <Play size={30} fill="currentColor" className="ml-1" />
            </motion.div>
          </div>
          <div className="absolute bottom-5 left-6 right-6 flex items-end justify-between">
            <div>
              <p className="font-mono text-[10px] tracking-[0.3em] text-hblue uppercase">Video Reel</p>
              <p className="font-display font-extrabold text-lg sm:text-xl mt-1">Live Performance Reel — Hulda Perez</p>
            </div>
            <span className="hidden sm:inline font-mono text-[10px] text-white/70 border border-white/20 rounded-full px-3 py-1">
              WATCH ON YOUTUBE
            </span>
          </div>
        </>
      )}
    </div>
  );
}

export default function Media() {
  return (
    <section id="media" className="relative py-24 lg:py-32" data-testid="media-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-16">
        <SectionHeading index="07" eyebrow="Media" title="The Reel" />

        <Reveal>
          <VideoBlock />
        </Reveal>

        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <Reveal delay={0.08} className="h-full">
            <div
              className="h-60 lg:h-full min-h-[240px] rounded-3xl border border-white/10 bg-white/[0.04] p-6 flex flex-col justify-between gap-6 transition-all duration-300 hover:border-horange/40"
              data-testid="media-audio-card"
            >
              <AudioLines size={22} className="text-horange" />
              <div>
                <h3 className="font-display text-xl font-extrabold tracking-tight">Audio Clips</h3>
                <p className="mt-2 text-sm text-hmut leading-relaxed">
                  Full songs, stems, and custom demos are shared directly with bookers.
                </p>
                <a
                  href={`mailto:${EMAIL}?subject=Audio%20reel%20request%20—%20Hulda%20Perez`}
                  data-testid="media-audio-request"
                  className="mt-4 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-horange hover:text-white transition-colors"
                >
                  <Mail size={13} /> Request audio
                </a>
              </div>
            </div>
          </Reveal>

          {[
            { id: "gallery-1", src: IMAGES.lounge, label: "Lounge & soul vocals" },
            { id: "gallery-2", src: IMAGES.guitar, label: "Featured on NYC Subway Series" },
            { id: "gallery-3", src: IMAGES.timesSquare, label: "NYC residencies" },
          ].map((g, i) => (
            <Reveal key={g.id} delay={0.12 + i * 0.08} className="h-full">
              <div
                className="group relative h-60 lg:h-full min-h-[240px] rounded-3xl overflow-hidden border border-white/10"
                data-testid={`media-photo-${i + 1}`}
              >
                <img
                  src={g.src}
                  alt={`Hulda Perez — ${g.label}`}
                  loading="lazy"
                  className="duotone absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-5 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-white/85">
                  <Camera size={12} /> {g.label}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-16">
          <Reveal>
            <div className="flex items-center gap-3 mb-8">
              <span className="h-px w-12 bg-gradient-to-r from-horange to-hblue" />
              <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-hdim">
                Songwriting Samples — 75-Second Previews
              </span>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 [&>*]:min-w-0">
            <Reveal>
              <AudioPlayer
                id="no-load"
                src="/audio/no-load-75s.mp3"
                title="No Load"
                credit="Written & Performed by Hulda Perez"
                tag="ORIGINAL"
              />
            </Reveal>
            <Reveal delay={0.1}>
              <AudioPlayer
                id="eagles"
                src="/audio/eagles-75s.mp3"
                title="Eagles"
                credit="Written & Arranged by Hulda Perez"
                tag="ARRANGEMENT"
              />
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
